// Pure builder for the brand create/update request body.
//
// Extracted from the inline payload assembled in
// client/src/pages/brand-wizard.tsx (handleSubmit). The load-bearing invariant:
// every editable Basics/Tone/Guardrails/Research field is sent as its value OR
// `null` (and array fields as the array OR `null` when empty) so CLEARING a
// field in the UI actually persists as null instead of being omitted (omitted
// fields leave the previous value untouched). Centralizing this guarantees a
// future field can't accidentally use the "omit when empty" pattern and silently
// fail to clear. `server/__tests__/brandPersonaConfig.test.ts` locks it in.

export interface BrandColorSwatch {
  label: string;
  hex: string;
}

export interface BrandWizardForm {
  name: string;
  slug: string;
  category: string;
  url: string;
  logoUrl: string;
  adgenLogoUrl: string;
  summary: string;
  positioning: string;
  worldview: string;
  enemy: string;
  promise: string;
  founderStory: string;
  mission: string;
  toneSummary: string;
  approvedPhrases: unknown;
  bannedPhrases: unknown;
  proofHierarchy: unknown;
  claimGuardrails: unknown;
  nonNegotiableTruths: unknown;
  visualRules: string;
  colorPalette: BrandColorSwatch[];
  categoryLanguage: unknown;
  pricePositioning: string;
  targetAudienceSignals: unknown;
  keyProducts: unknown;
  strategicPriorities: unknown;
  researchSources: unknown;
  researchNotes: string;
  researchConfidence: number | null | undefined;
  socialProfiles: string;
  brandDesignSystem: unknown;
}

export interface BuildBrandPayloadOptions {
  isEditMode: boolean;
  workspaceId?: string;
}

export function buildBrandUpdatePayload(
  form: BrandWizardForm,
  opts: BuildBrandPayloadOptions,
): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    name: form.name.trim(),
    slug:
      form.slug ||
      form.name
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/(^-|-$)/g, ""),
  };

  if (!opts.isEditMode) payload.workspaceId = opts.workspaceId;

  payload.category = form.category || null;
  payload.url = form.url || null;
  payload.logoUrl = form.logoUrl || null;
  payload.adgenLogoUrl = form.adgenLogoUrl || null;
  payload.summary = form.summary || null;
  payload.positioning = form.positioning || null;
  payload.worldview = form.worldview || null;
  payload.enemy = form.enemy || null;
  payload.promise = form.promise || null;
  payload.founderStory = form.founderStory || null;
  payload.mission = form.mission || null;
  payload.toneSummary = form.toneSummary || null;
  for (const key of BRAND_TAG_FIELDS) {
    const value = form[key];
    // Missing fields stay omitted; explicit clears of legacy tag lists still
    // persist as null. Structured and unsupported JSON round-trip losslessly.
    if (value !== undefined) payload[key] = Array.isArray(value) && !value.length ? null : value;
  }
  payload.visualRules = form.visualRules || null;
  const filteredPalette = form.colorPalette.filter((c) => c.label.trim() || c.hex.trim());
  payload.colorPalette = filteredPalette.length ? filteredPalette : null;

  payload.pricePositioning = form.pricePositioning || null;
  payload.researchNotes = form.researchNotes || null;
  payload.researchConfidence =
    typeof form.researchConfidence === "number" ? form.researchConfidence : null;
  if (form.socialProfiles && form.socialProfiles.trim()) {
    const sp = form.socialProfiles.trim();
    try {
      payload.socialProfiles = JSON.parse(sp);
    } catch {
      payload.socialProfiles = sp;
    }
  } else {
    payload.socialProfiles = null;
  }

  payload.brandDesignSystem = form.brandDesignSystem;

  return payload;
}

export const BRAND_TAG_FIELDS = [
  "approvedPhrases", "bannedPhrases", "proofHierarchy", "claimGuardrails",
  "nonNegotiableTruths", "categoryLanguage", "targetAudienceSignals",
  "keyProducts", "strategicPriorities", "researchSources",
] as const;
